/*
 * agent-a11y.js  -  Saachi Store   (v5)
 *
 * Names controls that would otherwise be invisible to screen readers and to the
 * agent/accessibility-tree audit in PageSpeed. Purely additive and defensive:
 * every entry point is wrapped in try/catch, and no styles, layout or behaviour
 * are touched. Delete this file and its <script> tag in layout/theme.liquid to
 * revert completely.
 *
 * v1 - homepage slideshow (Flickity): slide link names, and aria-hidden slides
 *      mirrored onto tabindex="-1".
 * v2 - cart drawer: icon-only controls and line-item thumbnails labelled.
 * v3 - invalid role="navigation" on the desktop nav <ul>, relocated to parent.
 * v4 - tabindex sync generalised beyond '.sslide' to any aria-hidden container
 *      (the '.gitem' product-carousel cell was being missed); unnamed search and
 *      cart-note fields; search landmark; duplicate id="quickadd"; empty lists.
 *
 * v5 - 2026-07-28. "Heading elements appear in a sequentially-descending order"
 *      was still failing. Two jumps on the homepage:
 *          H1 (logo) -> H5  "7-Day Easy Returns / Free UAE Delivery / 1-Year Warranty"
 *          H2        -> H4  "YOUR CART (0)" in the cart drawer
 *      The tags cannot be changed without editing several section files and
 *      risking the CSS that styles them (h5.ttlTxt and friends), so the level
 *      is corrected in the accessibility tree instead, via aria-level. Assistive
 *      technology and the audit both read aria-level in preference to the tag
 *      name, and nothing visual changes at all.
 */
(function () {
  'use strict';

  var SLIDE = '.sslide';
  var FOCUSABLE = 'a[href],button,input,select,textarea,iframe,[tabindex]';
  var STASH = 'data-a11y-prev-tabindex';

  function toList(nodes) {
    return Array.prototype.slice.call(nodes || []);
  }

  function hasAccessibleName(el) {
    if ((el.getAttribute('aria-label') || '').trim()) return true;
    if ((el.getAttribute('aria-labelledby') || '').trim()) return true;
    if ((el.getAttribute('title') || '').trim()) return true;
    if ((el.textContent || '').trim()) return true;
    var named = false;
    toList(el.querySelectorAll('img')).forEach(function (img) {
      if ((img.getAttribute('alt') || '').trim()) named = true;
    });
    return named;
  }

  function setName(el, label) {
    if (el && label && !hasAccessibleName(el)) el.setAttribute('aria-label', label);
  }

  /* ---------- v1: slideshow link names ---------- */

  function labelFromHref(href) {
    var key = '/collections/';
    var i = (href || '').indexOf(key);
    if (i < 0) return null;

    var rest = href.slice(i + key.length);
    ['?', '#', '/'].forEach(function (ch) {
      var j = rest.indexOf(ch);
      if (j > -1) rest = rest.slice(0, j);
    });
    if (!rest) return null;

    var parts;
    try { parts = decodeURIComponent(rest).split('-'); }
    catch (e) { parts = rest.split('-'); }
    parts = parts.filter(function (w) { return w.length > 0; });
    if (!parts.length) return null;

    return 'Shop ' + parts.map(function (w) {
      return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  }

  function nameSlideLinks() {
    toList(document.querySelectorAll(SLIDE + ' a[href]')).forEach(function (a) {
      setName(a, labelFromHref(a.getAttribute('href')));
    });
  }

  /* ---------- v4 (a): tabindex sync for every aria-hidden container ---------- */

  function syncTabindex() {
    toList(document.querySelectorAll('[aria-hidden="true"]')).forEach(function (host) {
      toList(host.querySelectorAll(FOCUSABLE)).forEach(function (el) {
        if (el.getAttribute('tabindex') === '-1') return;
        if (!el.hasAttribute(STASH)) {
          var prev = el.getAttribute('tabindex');
          el.setAttribute(STASH, prev === null ? '' : prev);
        }
        el.setAttribute('tabindex', '-1');
      });
    });

    toList(document.querySelectorAll('[' + STASH + ']')).forEach(function (el) {
      if (el.closest('[aria-hidden="true"]')) return;
      var restore = el.getAttribute(STASH);
      if (restore === '') el.removeAttribute('tabindex');
      else el.setAttribute('tabindex', restore);
      el.removeAttribute(STASH);
    });
  }

  /* ---------- v2: cart drawer ---------- */

  function iconClassOf(el) {
    var i = el.querySelector('i');
    return i ? (i.className || '') : '';
  }

  function lineItemName(a) {
    var row = a.closest('li') || a.parentElement;
    if (row) {
      var titleLink = row.querySelector('a[href*="/products/"]:not(.gitem-img)');
      var t = titleLink && (titleLink.textContent || '').trim();
      if (t) return t;
    }
    return 'View product';
  }

  function nameCartControls() {
    toList(document.querySelectorAll('a.qtyBtn, button.qtyBtn')).forEach(function (el) {
      var ic = iconClassOf(el);
      if (ic.indexOf('minus') > -1) setName(el, 'Decrease quantity');
      else if (ic.indexOf('plus') > -1) setName(el, 'Increase quantity');
    });

    toList(document.querySelectorAll('a.cftBtn, button.cftBtn')).forEach(function (el) {
      var href = el.getAttribute('href') || '';
      if (href.indexOf('cartNote') > -1) setName(el, 'Add an order note');
      else if (href.indexOf('cartCoupon') > -1) setName(el, 'Add a discount code');
      else setName(el, 'Cart option');
    });

    toList(document.querySelectorAll('a.gitem-img')).forEach(function (a) {
      setName(a, lineItemName(a));
    });
  }

  /* ---------- v3: invalid ARIA role on the desktop nav list ---------- */

  function relocateInvalidListRoles() {
    var lists = toList(document.querySelectorAll('ul[role="navigation"], ol[role="navigation"]'));

    lists.forEach(function (list) {
      if (list.closest('nav')) { list.removeAttribute('role'); return; }

      var parent = list.parentElement;
      if (!parent || parent.getAttribute('role')) return;

      parent.setAttribute('role', 'navigation');
      if (!parent.getAttribute('aria-label') && !parent.getAttribute('aria-labelledby')) {
        parent.setAttribute('aria-label', 'Main');
      }
      list.removeAttribute('role');
    });

    toList(document.querySelectorAll('[role="navigation"]')).forEach(function (el) {
      if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')) return;
      if ((el.className || '').toString().indexOf('mobile') > -1) {
        el.setAttribute('aria-label', 'Mobile');
      }
    });
  }

  /* ---------- v4 (b + c): unnamed form fields, search landmark ---------- */

  function fieldHasName(el) {
    if (hasAccessibleName(el)) return true;
    var id = el.getAttribute('id');
    if (id) {
      try {
        var esc = (window.CSS && CSS.escape) ? CSS.escape(id) : id;
        var lab = document.querySelector('label[for="' + esc + '"]');
        if (lab && (lab.textContent || '').trim()) return true;
      } catch (e) {}
    }
    var wrap = el.closest('label');
    if (wrap && (wrap.textContent || '').trim()) return true;
    return false;
  }

  function nameFormFields() {
    var sel = 'input:not([type="hidden"]):not([type="submit"]):not([type="button"]),' +
              'select, textarea';
    toList(document.querySelectorAll(sel)).forEach(function (f) {
      if (fieldHasName(f)) return;

      var name = (f.getAttribute('placeholder') || '').trim();
      if (!name) {
        var n = f.getAttribute('name');
        if (n === 'q') name = 'Search products';
        else if (n === 'note') name = 'Order notes for the seller';
        else name = (n || f.getAttribute('type') || 'text') + ' field';
      }
      f.setAttribute('aria-label', name);
    });
  }

  function markSearchLandmark() {
    toList(document.querySelectorAll('form[action*="/search"]')).forEach(function (form) {
      if (!form.getAttribute('role')) form.setAttribute('role', 'search');
    });
  }

  /* ---------- v4 (d): duplicate id="quickadd" ---------- */

  function isReferenced(id) {
    var attrs = ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls',
                 'aria-owns', 'aria-activedescendant', 'headers', 'list', 'form'];
    for (var i = 0; i < attrs.length; i++) {
      var found = document.querySelectorAll('[' + attrs[i] + ']');
      for (var j = 0; j < found.length; j++) {
        var v = found[j].getAttribute(attrs[i]) || '';
        if (v.split(/\s+/).indexOf(id) !== -1) return true;
      }
    }
    return !!document.querySelector('a[href="#' + id + '"]');
  }

  function dedupeIds() {
    var seen = {};
    toList(document.querySelectorAll('[id]')).forEach(function (el) {
      var id = el.getAttribute('id');
      if (!id) return;
      if (!seen[id]) { seen[id] = 1; return; }
      if (isReferenced(id)) { seen[id]++; return; }

      var n = seen[id] + 1;
      var next = id + '-' + n;
      while (document.getElementById(next)) { n++; next = id + '-' + n; }
      el.setAttribute('id', next);
      seen[id] = n;
    });
  }

  /* ---------- v4 (e): empty list containers ---------- */

  function markEmptyLists() {
    toList(document.querySelectorAll('ul, ol')).forEach(function (l) {
      var empty = !l.querySelector(':scope > li, :scope > [role="listitem"]');
      if (empty && !l.getAttribute('role')) {
        l.setAttribute('role', 'presentation');
        l.setAttribute('data-a11y-empty-list', '1');
      } else if (!empty && l.getAttribute('data-a11y-empty-list')) {
        l.removeAttribute('role');
        l.removeAttribute('data-a11y-empty-list');
      }
    });
  }

  /* ---------- v5: heading order ----------
   *
   * Walk the headings in document order and, wherever the level jumps by more
   * than one, pin the accessibility-tree level to previous + 1 using aria-level.
   *
   * Two deliberate details:
   *   - Headings that share the same tag as the one before them keep the SAME
   *     corrected level. The three trust badges are siblings, so they should
   *     read as three level-2 headings, not as 2 then 3 then 4.
   *   - Anything previously corrected is reset first, so a re-run after the
   *     cart drawer re-renders recomputes from scratch rather than drifting.
   */
  function fixHeadingOrder() {
    toList(document.querySelectorAll('[data-a11y-relevelled]')).forEach(function (h) {
      h.removeAttribute('aria-level');
      h.removeAttribute('data-a11y-relevelled');
    });

    var prevNatural = 0;
    var prevLevel = 0;

    toList(document.querySelectorAll('h1, h2, h3, h4, h5, h6')).forEach(function (h) {
      var natural = parseInt(h.tagName.charAt(1), 10);
      var level;

      if (natural === prevNatural) {
        level = prevLevel;                       // sibling: same level as its peer
      } else if (prevLevel && natural > prevLevel + 1) {
        level = prevLevel + 1;                   // jump: close the gap
      } else {
        level = natural;                         // already fine
      }

      if (level !== natural) {
        h.setAttribute('aria-level', String(level));
        h.setAttribute('data-a11y-relevelled', '1');
      }

      prevNatural = natural;
      prevLevel = level;
    });
  }

  /* ---------- watcher ---------- */

  var pending = null;
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; run(); }, 120);
  }

  function watchDocument() {
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  /* ---------- boot ---------- */

  function run() {
    try { nameSlideLinks(); }            catch (e) {}
    try { syncTabindex(); }              catch (e) {}
    try { nameCartControls(); }          catch (e) {}
    try { relocateInvalidListRoles(); }  catch (e) {}
    try { nameFormFields(); }            catch (e) {}
    try { markSearchLandmark(); }        catch (e) {}
    try { dedupeIds(); }                 catch (e) {}
    try { markEmptyLists(); }            catch (e) {}
    try { fixHeadingOrder(); }           catch (e) {}
  }

  function init() {
    run();
    try { watchDocument(); } catch (e) {}
    setTimeout(run, 1500);
    setTimeout(run, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
